import { I18NState, resolveTranslation, TranslateContext } from "..";

/**
 * Parses the input string or array of strings and returns an array of namespace and key tuples.
 *
 * @param input - The input string or array of strings to parse.
 * @returns An array of tuples where the first element is the namespace and the second element is the key.
 */
export function parseNamespaceAndKey(
  input: string | string[]
): [string, string][] {
  const i = Array.isArray(input) ? input : [input];

  return i.filter(Boolean).map((x) => {
    if (x.includes(" ")) {
      return [null, x];
    }

    const split = x.split(":");

    if (split.length === 1) {
      // console.warn(`@opendash/i18n: Namespace missing in '${x}'`);

      return [null, x];
    }

    if (split.length > 2) {
      console.warn(
        `@opendash/i18n: Don't use : in your translation keys in '${x}'.`
      );
    }

    const [namespace, key] = split;

    return [namespace, key];
  });
}

/**
 * Translates the input string or array of strings.
 *
 * @param input - The input string or array of strings to translate.
 * @param contextOrDefault - The context or default translation string.
 * @returns A promise that resolves to the translated string.
 */
export async function translate(
  input: string | string[],
  contextOrDefault?: string | TranslateContext
): Promise<string> {
  const ids = parseNamespaceAndKey(input);

  if (ids.length === 0) {
    return null;
  }

  await Promise.all(ids.map(([ns]) => resolveTranslation(ns)));

  return translateSync(input, contextOrDefault);
}

/**
 * Translates the input string or array of strings asynchronously.
 *
 * @param input - The input string or array of strings to translate.
 * @param contextOrDefault - The context or default translation string.
 * @returns A promise that resolves to the translated string.
 */
export function translateSync(
  input: string | string[],
  contextOrDefault?: string | TranslateContext
): string {
  const STATE: I18NState = I18NState.getInstance();
  const ids = parseNamespaceAndKey(input);

  if (ids.length === 0) {
    return null;
  }

  const context: TranslateContext =
    contextOrDefault === (contextOrDefault || "").toString()
      ? { default: contextOrDefault }
      : (contextOrDefault as TranslateContext) || {};

  let result = null;

  try {
    for (const [namespace, key] of ids) {
      if (!result && !namespace) {
        result = key;
      }

      // console.log(STATE.language, STATE.translations, namespace, key);

      if (namespace && !STATE.translations[STATE.language][namespace]) {
        // console.warn(`@opendash/i18n: Namespace not loaded yet '${namespace}'`);
        continue;
      }

      if (
        !result &&
        "count" in context === true &&
        "context" in context === true
      ) {
        result =
          STATE.translations[STATE.language][namespace][
            key + "_" + context.context + "_" + context?.count
          ];
      }

      if (
        !result &&
        "count" in context === true &&
        "context" in context === true
      ) {
        result =
          STATE.translations[STATE.language][namespace][
            key + "_" + context.context + "_" + context?.count
          ];
      }

      if (
        !result &&
        "count" in context === true &&
        "context" in context === true &&
        context.count !== 1
      ) {
        result =
          STATE.translations[STATE.language][namespace][
            key + "_" + context.context + "_plural"
          ];
      }

      if (!result && "context" in context === true) {
        result =
          STATE.translations[STATE.language][namespace][
            key + "_" + context.context
          ];
      }

      if (!result && "count" in context === true) {
        result =
          STATE.translations[STATE.language][namespace][
            key + "_" + context?.count
          ];
      }

      if (!result && "count" in context === true && context.count !== 1) {
        result = STATE.translations[STATE.language][namespace][key + "_plural"];
      }

      if (!result) {
        result = STATE.translations[STATE.language][namespace][key];
      }

      if (result) {
        break;
      }
    }
  } catch (e) {
    // console.error(e);
  }

  if (result || result === "") {
    for (const [key, value] of Object.entries(context)) {
      result = result.replace(`{{${key}}}`, value);
    }
  } else if ("default" in context) {
    result = context.default;
  } else {
    result = ids[0][1];
  }

  return result;
}
